import React from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { useLivePageContent } from '@/hooks/usePageContent';
import { ShieldCheck, ListTree } from 'lucide-react';

const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

marked.setOptions({ gfm: true, breaks: false });

// Heuristica: il corpo e' Markdown se contiene marcatori tipici (titoli, liste, tabelle, code-fence).
const looksMarkdown = (t) => /(^|\n)\s{0,3}(#{1,6}\s|[-*]\s|\|.*\||```)/.test(t);

// Rende il corpo di una sezione. Se Markdown -> HTML sanificato (titoli, liste, tabelle,
// blocchi pre-formattati). Altrimenti testo semplice preservando i ritorni a capo.
const Body = ({ text }) => {
  const raw = str(text);
  if (looksMarkdown(raw)) {
    const html = DOMPurify.sanitize(marked.parse(raw));
    return (
      <div
        className="legal-prose text-sm leading-relaxed text-muted-foreground"
        data-testid="legal-body-md"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  const paras = raw.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-3">
      {paras.map((p, i) => (
        <p key={i} className="text-sm leading-relaxed text-muted-foreground" style={{ whiteSpace: 'pre-line' }}>{p}</p>
      ))}
    </div>
  );
};

/**
 * Pagina legale riutilizzabile (Privacy / Cookie / Termini).
 * Contenuti di default in `config`; l'admin li sovrascrive dal CRM via /api/cms/page/{slug}
 * (campi: eyebrow, title, lead, sections[] = {heading, body}).
 */
export const LegalPage = ({ slug, config }) => {
  const ov = useLivePageContent(slug);
  const hasCms = Array.isArray(ov?.sections) && ov.sections.length > 0;

  const eyebrow = str(ov.eyebrow) || config.eyebrow || 'Note legali';
  const title = str(ov.title) || config.title;
  const lead = str(ov.lead) || config.lead;
  const sections = Array.isArray(ov.sections) && ov.sections.length ? ov.sections : config.sections;
  const updated = str(ov.updated) || config.updated;

  const indexItems = (sections || []).filter((s) => str(s.heading));
  const showIndex = indexItems.length > 1;
  const anchorId = (i) => `${slug}-sec-${i}`;

  const scrollTo = (i) => (e) => {
    e.preventDefault();
    const el = document.getElementById(anchorId(i));
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${anchorId(i)}`);
    }
  };

  return (
    <PublicLayout>
      <Seo title={title} description={lead} path={`/${slug}`} />
      <section className="sila-section pb-20 pt-32" data-testid={`legal-${slug}`}>
        <div className="mx-auto max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]">
            <ShieldCheck className="h-3.5 w-3.5" />{eyebrow}
          </span>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">{title}</h1>
          {lead && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{lead}</p>}
          {updated && <p className="mt-2 text-xs text-muted-foreground">Ultimo aggiornamento: {updated}</p>}

          {showIndex && (
            <nav className="mt-8 rounded-xl border border-border bg-muted/40 p-5" data-testid={`legal-index-${slug}`} aria-label="Indice documenti">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                <ListTree className="h-4 w-4" />Indice
              </div>
              <ol className="space-y-1.5">
                {indexItems.map((s, i) => (
                  <li key={i}>
                    <a
                      href={`#${anchorId(i)}`}
                      onClick={scrollTo(i)}
                      data-testid={`legal-index-link-${slug}-${i}`}
                      className="inline-flex items-start gap-2 text-sm text-primary transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                    >
                      <span className="mt-0.5 tabular-nums text-muted-foreground">{i + 1}.</span>
                      <span>{str(s.heading)}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {!hasCms && (
            <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/40 p-4 text-xs text-muted-foreground" data-testid={`legal-demo-note-${slug}`}>
              Testo dimostrativo. I contenuti legali definitivi verranno inseriti dall'amministratore dal pannello CRM.
            </div>
          )}

          <div className="mt-10 space-y-10">
            {(sections || []).map((s, i) => (
              <div key={i} id={anchorId(i)} className="scroll-mt-28" data-testid={`legal-section-${slug}-${i}`}>
                {str(s.heading) && <h2 className="mb-3 font-display text-xl font-semibold text-foreground">{str(s.heading)}</h2>}
                <Body text={s.body} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default LegalPage;
